import { useCallback, useState } from "react";
import { IMedication } from "@/types/medication";
import { memberMedicationService } from "@/services/member-medication.service";

export function useMemberMedications(memberId: string) {
  const [medications, setMedications] = useState<IMedication[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const fetchMedications = useCallback(async () => {
    setLoading(true);
    try {
      const meds = await memberMedicationService.getAllForMember(memberId);
      // Support for paginated or array response
      setMedications(Array.isArray(meds) ? meds : meds?.data ?? []);
      setFetched(true);
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  const addMedication = useCallback(
    async (med: IMedication | IMedication[]) => {
      setLoading(true);
      try {
        const meds = await memberMedicationService.createForMember(
          memberId,
          med
        );
        setMedications(meds);
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  const updateMedication = useCallback(
    async (medicationId: string, med: IMedication) => {
      setLoading(true);
      try {
        const meds = await memberMedicationService.updateForMember(
          memberId,
          medicationId,
          med
        );
        setMedications(meds);
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  const deleteMedication = useCallback(
    async (medicationId: string) => {
      setLoading(true);
      try {
        const meds = await memberMedicationService.deleteForMember(
          memberId,
          medicationId
        );
        setMedications(meds);
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  return {
    medications,
    loading,
    fetched,
    setFetched,
    fetchMedications,
    addMedication,
    updateMedication,
    deleteMedication,
  };
}
